import {
  useRef,
  useCallback,
  ReactNode,
  useState,
  useTransition,
  CSSProperties,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react'

import {
  Range,
  defaultRangeExtractor,
  useVirtualizer,
} from '@tanstack/react-virtual'
import { clsx } from 'clsx'
import { useInView } from 'react-intersection-observer'

import { useDebounce } from '../hooks/useDebounce.js'
import { useIsomorphicLayoutEffect } from '../hooks/useIsomorphicLayoutEffect.js'

export type VirtualListImperativeRef = {
  goToBottom: () => void
  scrollToItem: (key: string | number) => void
}

export interface ReverseVirtualListProps<T = unknown> {
  items: T[]
  getItemKey: (index: number) => string | number
  itemRenderer: (item: T, index: number, items: T[]) => ReactNode

  /**
   * itemComparator is used to detect if items are appended or prepended to the list,
   * which is necessary to  maintain scroll position when necessary.
   * Should return `true` if the items are equal, `false` otherwise.
   */
  itemComparator: (itemA: T, itemB: T) => boolean
  estimateSize: (index: number) => number
  overscan?: number
  onVisibleRangeChanged?: (range: Range) => void
  onHeadReached?: () => void
  onTailReached?: () => void
  initialIndex?: number
  autoTail?: boolean
  headerHeight?: number
  headerInnerComponent?: ReactNode
  footerHeight?: number
}

/**
 * header and footer elements
 */
const NUMBER_OF_EXTRA_VIRTUAL_ITEMS = 2

export const VirtualList = forwardRef<
  VirtualListImperativeRef,
  ReverseVirtualListProps
>((props, ref) => {
  const {
    items,
    getItemKey,
    estimateSize,
    itemRenderer,
    itemComparator,
    overscan = 30,
    onVisibleRangeChanged,
    onHeadReached,
    onTailReached,
    initialIndex,
    autoTail,
    headerHeight,
    headerInnerComponent,
    footerHeight = 10,
  } = props

  const [isInitialized, setInitialized] = useState(false)
  const isInitializedDebounced = useDebounce(isInitialized, 100)
  const [isPendingInitialization, startInitializing] = useTransition()

  const containerRef = useRef<HTMLDivElement>(null)

  const { ref: headerRef, inView: headerInView } = useInView({
    threshold: 0.5,
  })
  const { ref: footerRef, inView: footerInView } = useInView({
    threshold: 0.5,
  })

  const initialIndexProcessed = useRef(false)

  const topItemRef = useRef(items.at(0) ?? null)
  const bottomItemRef = useRef(items.at(-1) ?? null)
  const itemsCountRef = useRef(items.length ?? 0)
  const isPrepend = !itemComparator(topItemRef.current, items.at(0))
  const isAppend = !itemComparator(bottomItemRef.current, items.at(-1))
  const shouldTail = useRef(false)

  /**
   * Gets updated when header is scrolled into view to give us a point of comparison to
   * calculate the size difference when preserving scroll position after items
   * are prepended
   */
  const virtualizerSizeRef = useRef(null)

  const rangeExtractor = useCallback(
    (range: Range) => {
      if (onVisibleRangeChanged) {
        onVisibleRangeChanged({
          ...range,
          // Account for the header
          startIndex: range.startIndex - 1,

          endIndex: footerInView
            ? range.endIndex - NUMBER_OF_EXTRA_VIRTUAL_ITEMS
            : range.endIndex - 1,
          count: range.count - NUMBER_OF_EXTRA_VIRTUAL_ITEMS,
        })
      }
      return defaultRangeExtractor(range)
    },
    [footerInView, onVisibleRangeChanged],
  )

  const virtualizedCount = items.length + NUMBER_OF_EXTRA_VIRTUAL_ITEMS

  const shouldAdjustItemSize = useCallback(() => {
    if (initialIndexProcessed.current) {
      return false
    }
    if (!isInitializedDebounced) {
      return true
    }
    return isPrepend
  }, [isInitializedDebounced, isPrepend])

  const estimate = useCallback(
    (index: number) => {
      if (index === 0) {
        return headerHeight
      }
      if (index === virtualizedCount - 1) {
        return footerHeight
      }
      return estimateSize(index)
    },
    [estimateSize, footerHeight, headerHeight, virtualizedCount],
  )

  const virtualizer = useVirtualizer<HTMLDivElement, HTMLDivElement>({
    getScrollElement: () => containerRef.current,
    count: virtualizedCount,
    estimateSize: estimate,
    getItemKey,
    overscan,
    rangeExtractor,
    enabled: true,
    initialOffset: initialIndex ? initialIndex * estimateSize(1) : undefined,
    useScrollendEvent: true,
    isScrollingResetDelay: 500,
    onChange: virtualizer => {
      const { scrollDirection } = virtualizer

      if (scrollDirection === 'backward') {
        shouldTail.current = false
      } else if (footerInView) {
        if (autoTail) {
          shouldTail.current = true
        }
      }
    },
  })

  virtualizer.shouldAdjustScrollPositionOnItemSizeChange = shouldAdjustItemSize

  useEffect(() => {
    if (
      headerInView &&
      onHeadReached &&
      items.length === itemsCountRef.current &&
      !isPrepend
    ) {
      virtualizerSizeRef.current = virtualizer.getTotalSize()
      onHeadReached()
    }
    /**
     * This effect should only run if the state of headerInView gets changed.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerInView])

  useEffect(() => {
    if (
      footerInView &&
      onTailReached &&
      items.length === itemsCountRef.current &&
      !isAppend
    ) {
      onTailReached()
    }
    /**
     * This effect should only run if the state of footerInView gets changed.
     */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footerInView])

  useImperativeHandle(ref, () => ({
    scrollToItem: (key: string) => {
      shouldTail.current = false
      setTimeout(() => {
        const virtualItems = virtualizer.getVirtualItems()
        const item = virtualItems.find(item => item.key === key)
        if (item) {
          const isLastItem = item.index === itemsCountRef.current - 1
          requestAnimationFrame(() => {
            /**
             * @todo
             * If it's the last item, the scrollToIndex can't scroll to the bottom.
             */
            if (isLastItem) {
              virtualizer.scrollToOffset(virtualizer.getTotalSize(), {
                behavior: 'smooth',
              })
            } else {
              virtualizer.scrollToIndex(item.index + 1, {
                align: 'end',
                behavior: 'smooth',
              })
            }
          })
        }
      })
    },
    goToBottom: () => {
      shouldTail.current = true
      // goToBottom is called when new message is added to the list.
      // We need a first render pass to get the correct total size of the list.
      // after the new message is rendered and the total size is updated, we scroll to the bottom.
      setTimeout(() => {
        requestAnimationFrame(() => {
          virtualizer.scrollToOffset(virtualizer.getTotalSize(), {
            behavior: 'smooth',
          })
        })
      })
    },
  }))

  useIsomorphicLayoutEffect(() => {
    if (!isInitialized && !isPendingInitialization) {
      /**
       * When counts are equal, the virtualizer has settled and we can mark the list as initialized
       */
      startInitializing(() => {
        setInitialized(true)
        virtualizerSizeRef.current = virtualizer.getTotalSize()
      })
    }
  }, [])

  const virtualItems = virtualizer.getVirtualItems()

  if (initialIndexProcessed.current && isInitializedDebounced) {
    if (!shouldTail.current && isPrepend) {
      const preSize = virtualizerSizeRef.current
      /**
       * setTimeout is used to defer the calculation after the virtualizer has had at least one render pass.
       * The calculations would be wrong otherwise.
       */
      setTimeout(() => {
        const currentSize = virtualizer.getTotalSize()

        const delta = currentSize - preSize

        const offset = virtualizer.scrollOffset + delta
        virtualizer.scrollOffset = offset
        virtualizer.scrollToOffset(offset)
      })
    } else if (
      !virtualizer.isScrolling &&
      shouldTail.current &&
      !footerInView
    ) {
      requestAnimationFrame(() => {
        virtualizer.scrollToOffset(virtualizer.getTotalSize(), {
          behavior: 'smooth',
        })
      })
    }
  }

  topItemRef.current = items.at(0)
  bottomItemRef.current = items.at(-1)
  itemsCountRef.current = items.length

  useEffect(() => {
    if (!Number.isInteger(initialIndex)) {
      initialIndexProcessed.current = true
      return
    }

    if (initialIndexProcessed.current) {
      return
    }
    if (!isInitialized) {
      return
    }

    /**
     * + 1 to account for the header, + 1 to target the next element
     */
    const itemIndex = initialIndex + 2
    const isLast = itemIndex === virtualizer.options.count
    if (isLast) {
      virtualizer.scrollToOffset(virtualizer.getTotalSize())
      // virtualizer.scrollToIndex(itemIndex)
    } else {
      virtualizer.scrollToIndex(itemIndex)
      const [offset] = virtualizer.getOffsetForIndex(itemIndex, 'center')
      virtualizer.scrollOffset = offset
      virtualizer.scrollToOffset(offset)
    }
    initialIndexProcessed.current = true
    shouldTail.current = autoTail
  }, [initialIndex, isInitialized, virtualizer, autoTail])

  const transitionStyles: CSSProperties = {
    transition: 'opacity',
    transitionDuration: '300ms',
    transitionDelay: '100ms',
    transitionTimingFunction: 'ease-out',
    opacity: isInitializedDebounced ? 1 : 0,
  }

  return (
    <div
      className={clsx(
        'h-full overflow-y-auto w-full',
        !isInitializedDebounced && 'no-scrollbar',
      )}
      ref={containerRef}
      style={{
        contain: 'strict',
        position: 'relative',
        overscrollBehavior: 'none',
      }}
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          width: '100%',
          position: 'relative',
        }}
      >
        <div
          style={{
            ...transitionStyles,
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
          }}
        >
          {virtualItems.map(virtualItem => {
            const { index } = virtualItem
            if (index === 0) {
              return (
                <div
                  key="virtual-header"
                  ref={virtualizer.measureElement}
                  className={clsx(
                    'flex-1 justify-end',
                    !headerInnerComponent && 'hidden',
                  )}
                  style={{
                    ...transitionStyles,
                    height: headerHeight,
                    minHeight: headerHeight,
                    width: '100%',
                    position: 'relative',
                  }}
                  data-index={index}
                >
                  <div className="h-full w-full" ref={headerRef}>
                    {headerInnerComponent}
                  </div>
                </div>
              )
            }
            if (index === virtualizedCount - 1) {
              return (
                <div
                  key="virtual-footer"
                  ref={virtualizer.measureElement}
                  className="w-full"
                  style={{
                    height: footerHeight,
                    minHeight: footerHeight,
                    maxHeight: footerHeight,
                  }}
                  data-index={index}
                >
                  <div className="h-full w-full" ref={footerRef} />
                </div>
              )
            }

            const itemIndex = index - 1
            const item = items[itemIndex]
            return (
              <div
                key={`${virtualItem.key}`}
                ref={virtualizer.measureElement}
                data-index={index}
              >
                {itemRenderer(item, itemIndex, items)}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
})
